import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getControllableSessions,
  getItem,
  JellyfinRemoteSession,
  sendRemoteCommand,
  sendRemotePlay,
  sendRemotePlaystate,
} from '../api/jellyfin';
import { useSession } from './SessionProvider';
import { getRemoteSessionId, isControllableRemoteSession } from '../player/remotePlayback';

type RemotePlaybackValue = {
  sessions: JellyfinRemoteSession[];
  selectedSession: JellyfinRemoteSession | null;
  selectedSessionId: string | null;
  loading: boolean;
  commandPending: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  selectSession: (session: JellyfinRemoteSession | null) => void;
  playItem: (itemId: string, options?: { startOver?: boolean; mediaSourceId?: string }) => Promise<void>;
  playstate: (command: 'Pause' | 'Unpause' | 'Stop' | 'NextTrack' | 'PreviousTrack') => Promise<void>;
  seek: (positionTicks: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
  toggleMute: () => Promise<void>;
};

const RemotePlaybackContext = createContext<RemotePlaybackValue | null>(null);

export function RemotePlaybackProvider({ children }: PropsWithChildren) {
  const { credentials } = useSession();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [commandPending, setCommandPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedSeen = useRef(false);
  const sessionsQuery = useQuery({
    queryKey: ['remote-playback', 'sessions', credentials?.serverUrl, credentials?.userId],
    queryFn: () => getControllableSessions(credentials!),
    enabled: Boolean(credentials),
    refetchInterval: selectedSessionId ? 2_000 : 10_000,
    staleTime: 1_500,
    refetchOnWindowFocus: true,
  });
  const refreshSessions = sessionsQuery.refetch;
  const sessions = useMemo(
    () => (sessionsQuery.data ?? []).filter((entry) => credentials
      ? isControllableRemoteSession(entry, credentials.deviceId)
      : false),
    [credentials, sessionsQuery.data],
  );
  const selectedSession = useMemo(
    () => sessions.find((entry) => getRemoteSessionId(entry) === selectedSessionId) ?? null,
    [selectedSessionId, sessions],
  );

  useEffect(() => {
    setSelectedSessionId(null);
    selectedSeen.current = false;
    setError(null);
  }, [credentials?.serverUrl, credentials?.userId]);
  useEffect(() => {
    if (!selectedSessionId || !sessionsQuery.isSuccess) return;
    if (selectedSession) selectedSeen.current = true;
    else if (selectedSeen.current) {
      setSelectedSessionId(null);
      selectedSeen.current = false;
      setError('The remote player disconnected.');
    }
  }, [selectedSession, selectedSessionId, sessionsQuery.isSuccess]);

  const runCommand = useCallback(async (action: () => Promise<void>) => {
    setCommandPending(true);
    setError(null);
    try {
      await action();
      setTimeout(() => { void refreshSessions(); }, 350);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Remote playback command failed.';
      setError(message);
      throw reason;
    } finally {
      setCommandPending(false);
    }
  }, [refreshSessions]);

  const playItem = useCallback(async (
    itemId: string,
    options: { startOver?: boolean; mediaSourceId?: string } = {},
  ) => {
    if (!credentials || !selectedSessionId) return;
    await runCommand(async () => {
      const item = await getItem(credentials, itemId);
      const startPositionTicks = options.startOver ? 0 : item.UserData?.PlaybackPositionTicks ?? 0;
      await sendRemotePlay(credentials, selectedSessionId, [itemId], {
        startPositionTicks,
        mediaSourceId: options.mediaSourceId,
      });
    });
  }, [credentials, runCommand, selectedSessionId]);

  const playstate = useCallback(async (
    command: 'Pause' | 'Unpause' | 'Stop' | 'NextTrack' | 'PreviousTrack',
  ) => {
    if (!credentials || !selectedSessionId) return;
    await runCommand(() => sendRemotePlaystate(credentials, selectedSessionId, command));
  }, [credentials, runCommand, selectedSessionId]);

  const seek = useCallback(async (positionTicks: number) => {
    if (!credentials || !selectedSessionId) return;
    await runCommand(() => sendRemotePlaystate(credentials, selectedSessionId, 'Seek', positionTicks));
  }, [credentials, runCommand, selectedSessionId]);

  const setVolume = useCallback(async (volume: number) => {
    if (!credentials || !selectedSessionId) return;
    const next = Math.max(0, Math.min(100, Math.round(volume)));
    await runCommand(() => sendRemoteCommand(credentials, selectedSessionId, 'SetVolume', { Volume: String(next) }));
  }, [credentials, runCommand, selectedSessionId]);

  const toggleMute = useCallback(async () => {
    if (!credentials || !selectedSessionId) return;
    await runCommand(() => sendRemoteCommand(credentials, selectedSessionId, selectedSession?.PlayState?.IsMuted ? 'Unmute' : 'Mute'));
  }, [credentials, runCommand, selectedSession?.PlayState?.IsMuted, selectedSessionId]);

  const value = useMemo<RemotePlaybackValue>(() => ({
    sessions,
    selectedSession,
    selectedSessionId,
    loading: sessionsQuery.isLoading || sessionsQuery.isFetching,
    commandPending,
    error,
    refresh: async () => { await refreshSessions(); },
    selectSession: (session) => {
      setError(null);
      selectedSeen.current = Boolean(session);
      setSelectedSessionId(session ? getRemoteSessionId(session) : null);
    },
    playItem,
    playstate,
    seek,
    setVolume,
    toggleMute,
  }), [commandPending, error, playItem, playstate, refreshSessions, seek, selectedSession, selectedSessionId, sessions, sessionsQuery.isFetching, sessionsQuery.isLoading, setVolume, toggleMute]);

  return <RemotePlaybackContext.Provider value={value}>{children}</RemotePlaybackContext.Provider>;
}

export function useRemotePlayback(): RemotePlaybackValue {
  const value = useContext(RemotePlaybackContext);
  if (!value) throw new Error('useRemotePlayback must be used inside RemotePlaybackProvider.');
  return value;
}
